import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, type MemberStatus, type Profile } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';

type FilterKey = 'pending' | 'approved' | 'rejected' | 'all';

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'pending', label: '승인 대기' },
  { key: 'approved', label: '사용 중' },
  { key: 'rejected', label: '거절됨' },
  { key: 'all', label: '전체' },
];

const STATUS_META: Record<MemberStatus, { label: string; className: string }> = {
  pending: { label: '승인 대기', className: 'bg-accent-100 text-accent-800 border-accent-200' },
  approved: { label: '사용 중', className: 'bg-primary-100 text-primary-800 border-primary-200' },
  rejected: { label: '거절됨', className: 'bg-background-200 text-foreground-600 border-background-300' },
};

const ROLE_META: Record<string, { label: string; className: string }> = {
  admin: { label: '운영자', className: 'bg-secondary-100 text-secondary-900 border-secondary-200' },
  teacher: { label: '선생님', className: 'bg-primary-100 text-primary-800 border-primary-200' },
  student: { label: '학생', className: 'bg-accent-100 text-accent-900 border-accent-200' },
  user: { label: '일반', className: 'bg-background-100 text-foreground-600 border-background-300' },
};

function formatDate(value: string | null): string {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}.${mm}.${dd}`;
}

export default function AdminPage() {
  const { profile: me, signOut } = useAuth();
  const [members, setMembers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterKey>('pending');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<Profile | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });
      if (err) throw err;
      setMembers((data ?? []) as Profile[]);
    } catch {
      setError('회원 목록을 불러오지 못했어요. 잠시 후 다시 시도해 주세요.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2400);
    return () => clearTimeout(t);
  }, [toast]);

  const counts = useMemo(() => {
    const base = { pending: 0, approved: 0, rejected: 0, all: members.length };
    members.forEach((m) => {
      base[m.status] += 1;
    });
    return base;
  }, [members]);

  const visible = useMemo(
    () => (filter === 'all' ? members : members.filter((m) => m.status === filter)),
    [members, filter],
  );

  const updateStatus = async (id: string, status: MemberStatus) => {
    setBusyId(id);
    const approvedAt = status === 'approved' ? new Date().toISOString() : null;
    try {
      const { error: err } = await supabase
        .from('profiles')
        .update({ status, approved_at: approvedAt })
        .eq('id', id);
      if (err) throw err;
      setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, status, approved_at: approvedAt } : m)));
      setToast(status === 'approved' ? '회원을 승인했어요' : '회원을 거절했어요');
    } catch {
      setToast('상태 변경에 실패했어요. 잠시 후 다시 시도해 주세요.');
    } finally {
      setBusyId(null);
    }
  };

  const deleteMember = async (id: string) => {
    setBusyId(id);
    try {
      const { data, error: err } = await supabase.functions.invoke('admin-delete-user', {
        body: { userId: id },
      });
      if (err) throw err;
      const payload = data as { error?: string } | null;
      if (payload?.error) throw new Error(payload.error);
      setMembers((prev) => prev.filter((m) => m.id !== id));
      setToast('회원을 삭제했어요');
    } catch {
      setToast('삭제에 실패했어요. 잠시 후 다시 시도해 주세요.');
    } finally {
      setBusyId(null);
      setConfirmTarget(null);
    }
  };

  const filterClass = (key: FilterKey) =>
    `flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-bold transition ${
      filter === key ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:bg-background-200'
    }`;

  return (
    <div className="min-h-screen bg-background-100">
      {/* 헤더 */}
      <header className="sticky top-0 z-20 border-b border-background-200 bg-background-50/90 backdrop-blur">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between gap-4 px-4 py-3 md:px-6">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary-600 text-background-50">
              <i className="ri-shield-user-line text-lg"></i>
            </span>
            <div className="leading-tight">
              <h1 className="font-heading text-base font-bold text-foreground-950">운영자 페이지</h1>
              <p className="text-[11px] text-foreground-500">회원 승인 · 관리</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm font-semibold text-foreground-700 transition hover:bg-background-100"
            >
              <i className="ri-arrow-left-line text-base"></i>
              <span className="hidden sm:inline">오답노트로</span>
            </Link>
            <button
              type="button"
              onClick={() => void signOut()}
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm font-semibold text-foreground-700 transition hover:bg-background-100"
            >
              <i className="ri-logout-box-r-line text-base"></i>
              <span className="hidden sm:inline">로그아웃</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-4 py-6 md:px-6">
        {/* 요약 카드 */}
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4 md:gap-4">
          <div className="rounded-lg border border-accent-200 bg-accent-50 p-4">
            <p className="text-xs font-semibold text-accent-800">승인 대기</p>
            <p className="mt-1 font-heading text-2xl font-bold text-accent-900">{counts.pending}</p>
          </div>
          <div className="rounded-lg border border-primary-200 bg-primary-50 p-4">
            <p className="text-xs font-semibold text-primary-800">사용 중</p>
            <p className="mt-1 font-heading text-2xl font-bold text-primary-900">{counts.approved}</p>
          </div>
          <div className="rounded-lg border border-background-200 bg-white p-4">
            <p className="text-xs font-semibold text-foreground-600">거절됨</p>
            <p className="mt-1 font-heading text-2xl font-bold text-foreground-800">{counts.rejected}</p>
          </div>
          <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-4">
            <p className="text-xs font-semibold text-secondary-800">전체 회원</p>
            <p className="mt-1 font-heading text-2xl font-bold text-secondary-900">{counts.all}</p>
          </div>
        </div>

        {/* 필터 */}
        <div className="mb-5 flex flex-wrap items-center gap-1 rounded-full border border-background-200 bg-background-50 px-1 py-1 sm:w-fit">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" onClick={() => setFilter(f.key)} className={filterClass(f.key)}>
              {f.label}
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-bold ${
                  filter === f.key ? 'bg-background-50/25 text-background-50' : 'bg-secondary-100 text-secondary-800'
                }`}
              >
                {counts[f.key]}
              </span>
            </button>
          ))}
        </div>

        {/* 목록 */}
        <div className="rounded-lg border border-background-200 bg-white">
          <div className="flex items-center justify-between gap-3 border-b border-background-200 px-5 py-3.5">
            <h2 className="text-sm font-bold text-foreground-900">
              {FILTERS.find((f) => f.key === filter)?.label} 회원
            </h2>
            <button
              type="button"
              onClick={() => void load()}
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs font-semibold text-foreground-700 transition hover:bg-background-100"
            >
              <i className="ri-refresh-line text-sm"></i>
              새로고침
            </button>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
              <i className="ri-loader-4-line animate-spin text-2xl text-primary-600"></i>
              <p className="text-sm text-foreground-500">회원 목록을 불러오는 중이에요...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
              <i className="ri-error-warning-line text-2xl text-accent-600"></i>
              <p className="text-sm text-foreground-600">{error}</p>
              <button
                type="button"
                onClick={() => void load()}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition hover:bg-primary-600"
              >
                <i className="ri-refresh-line text-base"></i>
                다시 시도
              </button>
            </div>
          ) : visible.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-user-search-line text-2xl"></i>
              </span>
              <p className="text-sm text-foreground-500">해당하는 회원이 없어요</p>
            </div>
          ) : (
            <ul className="divide-y divide-background-200">
              {visible.map((m) => {
                const meta = STATUS_META[m.status];
                const isMe = m.id === me?.id;
                const busy = busyId === m.id;
                return (
                  <li key={m.id} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-500">
                        <i className="ri-user-3-line text-lg"></i>
                      </span>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="truncate text-sm font-semibold text-foreground-900">{m.email || '(이메일 없음)'}</p>
                          {(() => {
                            const roleMeta = ROLE_META[m.role] ?? ROLE_META.user;
                            return (
                              <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${roleMeta.className}`}>
                                {roleMeta.label}
                              </span>
                            );
                          })()}
                          {isMe && (
                            <span className="rounded-full border border-background-300 bg-background-100 px-2 py-0.5 text-[11px] font-bold text-foreground-600">
                              나
                            </span>
                          )}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-foreground-500">
                          <span className={`rounded-full border px-2 py-0.5 font-bold ${meta.className}`}>{meta.label}</span>
                          <span>신청일 {formatDate(m.created_at)}</span>
                          {m.approved_at && <span>· 승인일 {formatDate(m.approved_at)}</span>}
                        </div>
                      </div>
                    </div>

                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                      {m.status !== 'approved' && (
                        <button
                          type="button"
                          onClick={() => void updateStatus(m.id, 'approved')}
                          disabled={busy}
                          className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-2 text-xs font-bold text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <i className="ri-check-line text-sm"></i>
                          승인
                        </button>
                      )}
                      {m.status !== 'rejected' && (
                        <button
                          type="button"
                          onClick={() => void updateStatus(m.id, 'rejected')}
                          disabled={busy}
                          className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs font-bold text-foreground-700 transition hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <i className="ri-close-line text-sm"></i>
                          거절
                        </button>
                      )}
                      {!isMe && (
                        <button
                          type="button"
                          onClick={() => setConfirmTarget(m)}
                          disabled={busy}
                          className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-xs font-bold text-accent-800 transition hover:bg-accent-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <i className="ri-delete-bin-6-line text-sm"></i>
                          삭제
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <p className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
          <i className="ri-information-line mt-0.5 text-sm"></i>
          <span>
            승인하면 해당 회원이 오답노트를 사용할 수 있어요. 삭제하면 계정과 저장된 오답노트가 함께 사라지며 되돌릴 수 없어요.
          </span>
        </p>
      </main>

      {/* 삭제 확인 모달 */}
      {confirmTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/50 px-4">
          <div className="w-full max-w-[380px] rounded-lg border border-background-200 bg-white p-6">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                <i className="ri-delete-bin-6-line text-lg"></i>
              </span>
              <h3 className="font-heading text-base font-bold text-foreground-950">회원을 삭제할까요?</h3>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              <span className="font-bold text-foreground-800">{confirmTarget.email}</span> 님의 계정과 오답노트가 모두
              삭제됩니다. 이 작업은 되돌릴 수 없어요.
            </p>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={() => setConfirmTarget(null)}
                className="flex flex-1 cursor-pointer items-center justify-center whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100"
              >
                취소
              </button>
              <button
                type="button"
                onClick={() => void deleteMember(confirmTarget.id)}
                disabled={busyId === confirmTarget.id}
                className="flex flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-accent-600 px-4 py-2.5 text-sm font-bold text-background-50 transition hover:bg-accent-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busyId === confirmTarget.id ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-base"></i>
                    삭제 중...
                  </>
                ) : (
                  '삭제'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 토스트 */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2">
          <div className="flex items-center gap-2 whitespace-nowrap rounded-full bg-foreground-900 px-4 py-2.5 text-sm font-medium text-background-50">
            <i className="ri-checkbox-circle-line text-base text-primary-300"></i>
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}